import styles from '../GenerationWorkspace.module.css';
import type { AttributeTreeNode } from '../state/types';
import type { BrandWithUrls } from '../../../features/brands/urls';
import { HierarchicalSelect, type HierarchicalOption } from '../../common/HierarchicalSelect';
import { TemplateUrlPicker } from '../TemplateUrlPicker';
import { findNodeByKey as findSelectedNode } from '../utils/templateCapture';

export type ActiveTemplateInfo = {
  group: AttributeTreeNode;
  optionKey: string;
  label: string;
  captureRequired: boolean;
};

/**
 * テンプレートセクション (section='template' のトップレベル group) 1 件分のアコーディオン。
 * GenerationWorkspace の JSX からの切り出しで、DOM 構造・クラス名は不変。
 */
export function TemplateSection({
  node,
  order,
  isOpen,
  onToggle,
  selectedKey,
  activeTemplate,
  onSelectOption,
  templateReferenceUrl,
  onChangeTemplateReferenceUrl,
  brandUrlChoices,
}: {
  node: AttributeTreeNode;
  order: number;
  isOpen: boolean;
  onToggle: () => void;
  /** settings.attributes[node.key] (文字列以外は 'none') */
  selectedKey: string;
  activeTemplate: ActiveTemplateInfo | null;
  onSelectOption: (groupKey: string, optionKey: string) => void;
  templateReferenceUrl: string;
  onChangeTemplateReferenceUrl: (url: string) => void;
  brandUrlChoices: BrandWithUrls[] | undefined;
}) {
  const isThisGroupActive = activeTemplate?.group.key === node.key;
  const categories = (node.children ?? []).filter((child) => child.type === 'group');

  return (
    <div
      className={`accordion-section ${isOpen ? 'accordion-section-open' : ''}`}
      style={{ order }}
    >
      <button
        type="button"
        className="accordion-header"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="accordion-title">
          <img src="/design_file_text_image.svg" alt="" className="accordion-title-icon" />
          <span>{node.label}</span>
          {isThisGroupActive && (
            <span className="accordion-summary">{activeTemplate?.label}</span>
          )}
        </span>
        <span className={`accordion-icon ${isOpen ? 'accordion-icon-open' : ''}`}>
          {isOpen ? '▼' : '▶'}
        </span>
      </button>
      <div className={`accordion-panel ${isOpen ? 'accordion-panel-open' : 'accordion-panel-closed'}`}>
        <div className={styles.templateCategoryGrid}>
          {categories.map((category) => {
            const belongs =
              !!findSelectedNode(category.children, selectedKey) || category.key === selectedKey;
            return (
              <HierarchicalSelect
                key={category.id}
                label={category.label}
                options={(category.children ?? []) as HierarchicalOption[]}
                value={belongs ? selectedKey : 'none'}
                onChange={(key) => onSelectOption(node.key, key)}
              />
            );
          })}
        </div>
        {/* キャプチャ必須テンプレート (ポスター等) の参照 URL 入力。
            ポスター等の値に従属する設定なので、プラットフォーム→メニューと同じ
            インデントバー (.nestedSection) で囲んで所属を示す。
            主導線は URL 直接入力、副導線がブランド→サイト選択。 */}
        {isThisGroupActive && activeTemplate?.captureRequired && (
          <div className={`${styles.nestedSection} ${styles.nestedSectionVisible} ${styles.templateUrlNested}`}>
            {/* 主導線: URL 直接入力 */}
            <div className={styles.templateUrlField}>
              <label className={styles.templateUrlLabel} htmlFor={`template-url-${node.key}`}>
                URL
              </label>
              <input
                id={`template-url-${node.key}`}
                type="url"
                className={styles.templateUrlInput}
                placeholder="https://example.com/..."
                value={templateReferenceUrl}
                onChange={(e) => onChangeTemplateReferenceUrl(e.target.value)}
              />
            </div>

            {/* 副導線: 登録済みブランド → サイトから選んで URL 欄に反映 (＋でブランド追加) */}
            {(brandUrlChoices ?? []).length > 0 && (
              <div className={styles.templateUrlSubPath}>
                <div className={styles.templateUrlOrDivider}>または</div>
                <span className={styles.templateUrlSubLabel}>
                  登録済みブランドのURLから選ぶ
                </span>
                <TemplateUrlPicker
                  brands={brandUrlChoices ?? []}
                  value={templateReferenceUrl}
                  onChange={onChangeTemplateReferenceUrl}
                />
              </div>
            )}

            <p className={styles.templateUrlHint}>
              ※既存のキャプチャ画像を素材に追加することもできます。
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
